/**
 * kanban/constants.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for every static value used across the Kanban feature.
 * Changing a colour, label, or column order only requires editing this file.
 */

// ── Column definitions ────────────────────────────────────────────────────────
// `statusKey`   — must match the task.status values returned by the API.
// `accentColor` — bottom border on funnel cards / accent bar under column header.
// `labelClass`  — Tailwind text colour for the column heading.
// `badgeBg`     — Tailwind bg class for the task-count badge.
// `badgeText`   — Tailwind text class for the task-count badge.
// `cardBorder`  — Tailwind border class applied to every card in this column.
// `footerIcon`  — Material Symbol name shown in the card footer's effort row.
// `footerColor` — Tailwind text class for the footer effort/status label.
// `footerLabel` — Static label shown when there is no effort data (On Hold / Cancelled).
export const KANBAN_COLUMNS = [
    {
        key: 'Backlog',
        label: 'Backlog',
        accentColor: '#c2c6d2',
        labelClass: 'text-slate-500 dark:text-slate-400',
        badgeBg: 'bg-[#e7e8e9] dark:bg-[#334155]',
        badgeText: 'text-[#424750] dark:text-slate-300',
        cardBorder: 'border-[#e7e8e9] dark:border-slate-700',
        footerIcon: 'timer',
        footerColor: 'text-[#424750] dark:text-slate-400',
        footerLabel: null,
        emptyMessage: 'No backlog tasks',
    },
    {
        key: 'In Progress',
        label: 'In Progress',
        accentColor: '#00AAF1',
        labelClass: 'text-[#03488A] dark:text-[#a7c8ff]',
        // Badge uses the brand gradient — handled inline in KanbanColumn
        badgeBg: '__brand_gradient__',
        badgeText: 'text-white',
        cardBorder: 'border-blue-100 dark:border-blue-900/40 border-l-4 border-l-[#03488A] dark:border-l-[#a7c8ff]',
        footerIcon: 'timer',
        footerColor: 'text-[#03488A] dark:text-[#a7c8ff]',
        footerLabel: null,
        emptyMessage: 'Nothing in progress',
    },
    {
        key: 'Under Review',
        label: 'Under Review',
        accentColor: '#f59e0b',
        labelClass: 'text-amber-600 dark:text-amber-400',
        badgeBg: 'bg-amber-300',
        badgeText: 'text-amber-800',
        cardBorder: 'border-amber-100 dark:border-amber-900/30',
        footerIcon: 'timer',
        footerColor: 'text-amber-600 dark:text-amber-400',
        footerLabel: null,
        emptyMessage: 'No tasks under review',
    },
    {
        key: 'Completed',
        label: 'Completed',
        accentColor: '#34d399',
        labelClass: 'text-emerald-600 dark:text-emerald-400',
        badgeBg: 'bg-emerald-400',
        badgeText: 'text-white',
        cardBorder: 'border-emerald-100 dark:border-emerald-900/30',
        footerIcon: 'check_circle',
        footerColor: 'text-emerald-600 dark:text-emerald-400',
        footerLabel: null,
        emptyMessage: 'No completed tasks yet',
    },
    {
        key: 'On Hold',
        label: 'On Hold',
        accentColor: '#fb923c',
        labelClass: 'text-orange-600 dark:text-orange-400',
        badgeBg: 'bg-orange-300',
        badgeText: 'text-orange-900',
        cardBorder: 'border-orange-100 dark:border-orange-900/30',
        footerIcon: 'block',
        footerColor: 'text-slate-400 dark:text-slate-500',
        footerLabel: 'Blocked',
        emptyMessage: 'Nothing on hold',
    },
    {
        key: 'Cancelled',
        label: 'Cancelled',
        accentColor: '#f87171',
        labelClass: 'text-red-600 dark:text-red-400',
        badgeBg: 'bg-red-300',
        badgeText: 'text-red-900',
        cardBorder: 'border-red-100 dark:border-red-900/30',
        footerIcon: 'cancel',
        footerColor: 'text-red-600 dark:text-red-400',
        footerLabel: 'Dropped',
        emptyMessage: 'No cancelled tasks',
    },
];

// ── Priority badge styles ─────────────────────────────────────────────────────
export const PRIORITY_BADGE = {
    Critical: {
        bg: 'bg-[#ffdad6]',
        text: 'text-[#93000a]',
        dot: 'bg-[#93000a]',
    },
    High: {
        bg: 'bg-red-50',
        text: 'text-red-600',
        dot: 'bg-red-500',
    },
    Medium: {
        bg: 'bg-[#c9e6ff]',
        text: 'text-[#004c6e]',
        dot: 'bg-[#006591]',
    },
    Low: {
        bg: 'bg-[#e7e8e9]',
        text: 'text-[#424750]',
        dot: 'bg-slate-400',
    },
};

// ── Effort progress-bar colours per status ────────────────────────────────────
export const EFFORT_BAR_COLOR = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
};

// ── Column min width ──────────────────────────────────────────────────────────
export const COLUMN_MIN_WIDTH = 288; // px — keeps cards readable on smaller screens
